import {
  EVENT_STATUS_LABELS,
  EVENT_TYPE_LABELS,
  formatDateOnly,
  toFacilityDate,
  type EventStatus,
  type EventType,
  type StatusTone,
} from "@alm/domain";
import type { orpcClient, Output } from "@/lib/orpc";

/** Derived from the API rather than retyped beside it. */
export type EventRow = Output<typeof orpcClient.crm.events.list>["rows"][number];
export type EventDetail = Output<typeof orpcClient.crm.events.get>;

export const EVENT_VIEWS = ["month", "week", "list", "board", "activity"] as const;
export type EventView = (typeof EVENT_VIEWS)[number];
export const EVENT_VIEW_KEY = "crm-events-view";

const EVENT_TONES: Record<EventStatus, StatusTone> = {
  planned: "neutral",
  confirmed: "info",
  completed: "ok",
  cancelled: "crit",
};

export const statusLabelOf = (status: string) =>
  EVENT_STATUS_LABELS[status as EventStatus] ?? status;
export const typeLabelOf = (type: string) => EVENT_TYPE_LABELS[type as EventType] ?? type;
export const toneOf = (status: string): StatusTone =>
  EVENT_TONES[status as EventStatus] ?? "neutral";

/** The day an instant falls on, read on the given clock. */
export const dayIn = (instant: string | Date, zone: string) =>
  toFacilityDate(new Date(instant), zone);

/** "2:30 PM" on the given clock, for the calendar chips. */
export function clockIn(instant: string | Date, zone: string): string {
  return clockFor(instant, zone);
}

/** "14:30" on the given clock: what a time input holds. */
export function clockValueIn(instant: string | Date, zone: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: zone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(instant));
}

const clockFor = (instant: string | Date, zone: string) =>
  new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(instant));

/** "Oct 10, 2026, 2:00 PM – 5:00 PM", or "Oct 10, 2026 (all day)". */
export function eventWhen(
  event: { startsAt: string | Date; endsAt: string | Date | null; allDay: boolean },
  zone: string,
): string {
  const day = formatDateOnly(event.startsAt, zone);
  if (event.allDay) {
    const last = event.endsAt ? formatDateOnly(event.endsAt, zone) : day;
    return last === day ? `${day} (all day)` : `${day} – ${last} (all day)`;
  }
  const start = clockFor(event.startsAt, zone);
  if (!event.endsAt) return `${day}, ${start}`;
  const sameDay = dayIn(event.endsAt, zone) === dayIn(event.startsAt, zone);
  const end = sameDay
    ? clockFor(event.endsAt, zone)
    : `${formatDateOnly(event.endsAt, zone)}, ${clockFor(event.endsAt, zone)}`;
  return `${day}, ${start} – ${end}`;
}

/** Where it is, as one line. */
export const placeOf = (event: { locationName: string | null; address: string | null }) =>
  [event.locationName, event.address].filter(Boolean).join(", ");

export const assigneeNames = (event: { assignees: Array<{ name: string }> }) =>
  event.assignees.map((person) => person.name).join(", ");
